import { gql } from '@apollo/client';
import React from 'react';
import EmptyState from '../components/EmptyState';
import GroupMention from '../editor/mentions/GroupMention';
import PostMention from '../editor/mentions/PostMention';
import TopicMention from '../editor/mentions/TopicMention';
import UserMention from '../editor/mentions/UserMention';
import type { PostInsightsBacklinks_PostFragment } from '../graphql/generated/graphqlTypes';
import styles from './PostInsightsBacklinks.module.scss';

const POST_FRAGMENT = gql`
  fragment PostInsightsBacklinks_post on Post {
    id
    backlinks {
      ... on Post {
        id
        title
        type
      }
      ... on User {
        id
        name
      }
      ... on Group {
        id
        name
      }
      ... on Topic {
        id
        name
      }
    }
  }
`;

interface PostInsightsBacklinksProps {
  post: PostInsightsBacklinks_PostFragment;
  showHoverCard?: boolean;
  newTab?: boolean;
}

const ORDER = ['post', 'topic', 'user', 'group', 'template'];

const PostInsightsBacklinks = ({
  post,
  showHoverCard,
  newTab,
}: PostInsightsBacklinksProps) => {
  const backlinks = [...(post.backlinks ?? [])].sort((a, b) => {
    const aType =
      (a.__typename === 'Post' ? a.type : a.__typename)?.toLowerCase() ?? '';
    const bType =
      (b.__typename === 'Post' ? b.type : b.__typename)?.toLowerCase() ?? '';

    return ORDER.indexOf(aType) - ORDER.indexOf(bType);
  });

  return (
    <div className={styles.backlinks}>
      {backlinks.length === 0 && (
        <div className={styles.empty}>
          <EmptyState
            title="No backlinks yet"
            message="Mention this post somewhere to add a backlink"
            imageName="connections"
            vertical
            smallText
            noMargin
          />
        </div>
      )}
      {backlinks.map((backlink) => (
        <div key={backlink.id} className={styles.backlink}>
          {backlink.__typename === 'Post' && (
            <PostMention
              id={backlink.id}
              content={backlink.title}
              link
              singleLine
              showHoverCard={showHoverCard}
              newTab={newTab}
            />
          )}
          {backlink.__typename === 'Topic' && (
            <TopicMention
              id={backlink.id}
              content={backlink.name}
              link
              singleLine
              showHoverCard={showHoverCard}
              newTab={newTab}
            />
          )}
          {backlink.__typename === 'User' && (
            <UserMention
              id={backlink.id}
              content={backlink.name}
              link
              singleLine
              showHoverCard={showHoverCard}
              newTab={newTab}
            />
          )}
          {backlink.__typename === 'Group' && (
            <GroupMention
              id={backlink.id}
              content={backlink.name}
              link
              singleLine
              showHoverCard={showHoverCard}
              newTab={newTab}
            />
          )}
        </div>
      ))}
    </div>
  );
};
PostInsightsBacklinks.fragments = {
  post: POST_FRAGMENT,
};

export default PostInsightsBacklinks;
